import { Link, useRouter } from "@tanstack/react-router";
import { Button } from "@papier/ui/components/ui/button";
import { Folder, LayoutDashboard, Plus } from "lucide-react";
import type { ReactNode } from "react";

export function NotFound({ children }: { children?: ReactNode }) {
  const router = useRouter();

  return (
    <div className="relative flex min-h-[80vh] flex-col items-center justify-center bg-transparent px-4">
      <h1 className="mb-2 text-7xl font-extrabold">404</h1>
      <h2 className="mb-4 text-2xl font-semibold">Oops! Page not found.</h2>
      <p className="mb-8 max-w-lg text-center text-slate-600 dark:text-slate-400">
        {children || "The page you are looking for does not exist or has been moved."}
      </p>
      <div className="space-y-2 p-2">
        <p className="flex flex-wrap items-center gap-2">
          <Button onClick={() => router.history.back()} size="lg">
            Go back
          </Button>

          <Button asChild variant="secondary" size="lg">
            <Link to="/">Go Home</Link>
          </Button>
        </p>
      </div>
    </div>
  );
}

export function FilteredProjectsNotFound() {
  return (
    <div className="text-foreground bg-card flex h-[30vh] flex-col items-center justify-center rounded-lg">
      <Folder className="mb-4 size-12" />
      <p className="text-lg font-medium">No projects found</p>
      <p className="mt-1 text-sm">Try adjusting your filters or search query.</p>
    </div>
  );
}

export function NoProjectsFound({ openCreateWizard }: { openCreateWizard: () => void }) {
  return (
    <div className="text-foreground bg-card flex h-[30vh] flex-col items-center justify-center rounded-lg">
      <Folder className="mb-1 size-12" />
      <p className="pb-3 text-lg font-medium">No projects found</p>
      <Button
        onClick={openCreateWizard}
        className="glass-button border-white/20 text-white hover:text-white"
      >
        <Plus className="size-4" />
        Create New Project
      </Button>
    </div>
  );
}

export function ProjectNotFound({ message }: { message?: string }) {
  const router = useRouter();
  return (
    <div className="text-foreground glass-card flex h-[30vh] flex-col items-center justify-center rounded-lg">
      <Folder className="mb-4 size-12" />
      <p className="text-lg font-medium">Project not found</p>
      <p className="text-foreground/70 mt-1">
        {message || "The project does not exist or you don't have access to it."}
      </p>
      <div className="mt-4 space-x-2">
        <Button onClick={() => router.history.back()} variant="outline">
          Go Back
        </Button>
      </div>
    </div>
  );
}

export function TemplateNotFound({ message }: { message?: string }) {
  const router = useRouter();
  return (
    <div className="text-foreground glass-card flex h-[30vh] flex-col items-center justify-center rounded-lg">
      <LayoutDashboard className="mb-4 size-12" />
      <p className="text-lg font-medium">Template not found</p>
      <p className="text-foreground/70 mt-1">
        {message || "The template does not exist or you don't have access to it."}
      </p>
      <div className="mt-4 space-x-2">
        <Button onClick={() => router.history.back()} variant="outline">
          Go Back
        </Button>
      </div>
    </div>
  );
}

export function FilteredTemplatesNotFound() {
  return (
    <div className="text-foreground glass-card flex h-[30vh] flex-col items-center justify-center rounded-lg">
      <LayoutDashboard className="mb-4 size-12" />
      <p className="text-lg font-medium">No templates found</p>
      <p className="mt-1 text-sm">Try adjusting your filters or search query.</p>
    </div>
  );
}

export function NoTemplatesFound({ openCreateWizard }: { openCreateWizard: () => void }) {
  return (
    <div className="text-foreground glass-card flex h-[30vh] flex-col items-center justify-center rounded-lg">
      <LayoutDashboard className="mb-1 size-12" />
      <p className="pb-3 text-lg font-medium">No templates found</p>
      <Button
        onClick={openCreateWizard}
        className="glass-button border-white/20 text-white hover:text-white"
      >
        <Plus className="size-4" />
        Create Template
      </Button>
    </div>
  );
}
